import React, { useEffect, useState } from "react";
import { Download, X, Wifi, WifiOff, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * PWAInstallBanner — shows a soft install prompt + offline indicator.
 * - Captures `beforeinstallprompt` (Chrome/Edge/Android)
 * - Shows iOS-specific instructions on iPhone/iPad (no native prompt)
 * - Shows offline status when navigator.onLine is false
 * - Dismissible per session
 */
export default function PWAInstallBanner() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [iosHint, setIosHint] = useState(false);
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem("pwa_install_dismissed") === "1");

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); };
    const onlineHandler = () => setOnline(true);
    const offlineHandler = () => setOnline(false);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    window.addEventListener("online", onlineHandler);
    window.addEventListener("offline", offlineHandler);

    // iOS detection
    const ua = navigator.userAgent.toLowerCase();
    const isIos = /iphone|ipad|ipod/.test(ua);
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    if (isIos && !isStandalone) setIosHint(true);
    if (isStandalone) setInstalled(true);

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      window.removeEventListener("online", onlineHandler);
      window.removeEventListener("offline", offlineHandler);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const result = await deferred.userChoice;
    if (result.outcome === "accepted") setInstalled(true);
    setDeferred(null);
  };

  const dismiss = () => {
    sessionStorage.setItem("pwa_install_dismissed", "1");
    setDismissed(true);
  };

  return (
    <>
      {!online && (
        <div data-testid="pwa-offline-banner" className="bg-amber-500/15 border-b border-amber-500/30 px-4 py-2 flex items-center gap-2 text-xs">
          <WifiOff className="w-3.5 h-3.5 text-amber-400 shrink-0" />
          <span className="text-amber-300">Offline — your work will sync when connection returns.</span>
        </div>
      )}
      {online && installed && (
        <div data-testid="pwa-installed-badge" className="bg-emerald-500/10 border-b border-emerald-500/20 px-4 py-1.5 flex items-center gap-2 text-[11px]">
          <Smartphone className="w-3 h-3 text-emerald-400" />
          <span className="text-emerald-300 uppercase tracking-wide font-bold">Installed App</span>
          <Wifi className="w-3 h-3 text-emerald-400 ml-auto" />
        </div>
      )}
      {!installed && !dismissed && (deferred || iosHint) && (
        <div data-testid="pwa-install-banner" className="bg-gradient-to-r from-blue-500/15 to-cyan-500/10 border-b border-blue-500/30 px-4 py-3 flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-blue-500/20 border border-blue-500/40 flex items-center justify-center shrink-0">
            <Smartphone className="w-4 h-4 text-blue-300" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-blue-200">Install TrueLine Tech</div>
            {iosHint ? (
              <div className="text-[11px] text-slate-400 mt-1 leading-relaxed">
                Tap <span className="font-semibold text-blue-300">Share</span> ⤴, then <span className="font-semibold text-blue-300">"Add to Home Screen"</span>. Works offline & looks like a native app.
              </div>
            ) : (
              <>
                <div className="text-[11px] text-slate-400 mt-0.5">Adds icon to your home screen · works offline · faster.</div>
                <Button data-testid="pwa-install-btn" size="sm" onClick={install} className="mt-2 bg-blue-600 hover:bg-blue-500 h-7 text-xs">
                  <Download className="w-3 h-3 mr-1.5" /> Install App
                </Button>
              </>
            )}
          </div>
          <button data-testid="pwa-dismiss-btn" onClick={dismiss} className="text-slate-500 hover:text-slate-300 shrink-0"><X className="w-4 h-4" /></button>
        </div>
      )}
    </>
  );
}
